import { DataLoader } from '../../core/types/data-loader';
import {
  getSensitiveRoute,
  getLastVisitedRoute,
  navigateToExternal,
  parseCurrentQueryString,
  currentRouteToHash,
  mergeTyped,
  getRouteFromHash
} from '../../core/util';
import config from '../../config';
import {
  PartialStateStore,
  StateStore,
  StateStoreOrVoid
} from '../../core/types/application-state';
import {
  convertAppDataToProfileData,
  convertProfileDataToAppData
} from '../shared/services/util';
import {
  createNewJourneyProfile,
  getApplicationDetailsByID,
  saveJourneyProfile,
  getAllApplicationsByEmail
} from '../shared/services/profile-service';
import { loadUser } from '../shared/loaders';
import {
  consumerTypes,
  getAppSourceType,
  removeAppendedReadableStepString,
  appendReadableStepString,
  applicationStatusTypes,
  parseAccountNumbers,
  isNonFundedFlow,
  accountTypes,
  getAgreementType,
  chargeTypes,
  redirectToAccountFinder
} from '../shared/utils';
import {
  loadElectronicAccessDeliveryTerms,
  loadElectronicAccessTerms,
  loadFiduciaryDisclosureTerms
} from '../shared/services/terms-service';
import { unsubscribeFromRJProfile } from '../shared/services/rj-profile-unsubscribe';
import { ServiceError } from '../shared/services';
import routeMap from '../../indexed-route-map';
import {
  analyticsDataLayer,
  dispatchPageLoad
} from '../../analytics/analytics-helper';
import { getExistingAgreementsService } from '../shared/services/existing-agreements-service';

export const commitProfileEmail = async ({
  inputs
}): Promise<PartialStateStore> => {
  const { primaryEmailAddress } = inputs;
  return {
    routeData: {
      queryString: `?email=${primaryEmailAddress}`
    }
  };
};

export const applicationAlreadyExists = (
  errorStr,
  serviceErrorCode
): boolean => {
  if (errorStr.match(/Not authorized to view application/)) {
    return true;
  }
  if (serviceErrorCode === 80005) {
    return true;
  } /* check if error code refers to "Application already exists" */
  return false;
};

export const getFirst = (field): any =>
  Array.isArray(field) ? field[0] : field || '';

export const getQueryStringData = (): any => {
  const qs = parseCurrentQueryString();
  const email = getFirst(qs.email);
  const applicationID = getFirst(qs.applicationID);
  const loginrequired = getFirst(qs.loginrequired) === 'true';
  return { email, applicationID, loginrequired };
};

export const getJourneyProfile = async ({
  profileID,
  store
}): Promise<PartialStateStore> => {
  const profileData = await getApplicationDetailsByID(profileID);
  const appData = await convertProfileDataToAppData(profileData, store);
  return appData;
};

export const setProgressStepText = (store: StateStore): string => {
  const { progressStepText: lastProgressStepText } = store;
  const lastHashInlastProgressStepText = lastProgressStepText?.substring(
    lastProgressStepText.length - 6
  );
  // check if next route to be stored to progress step is credentials
  const isCredentialsRoute = getRouteFromHash(currentRouteToHash())?.includes(
    'credentials'
  );

  // if the current route's hash already exists at the end of progressStepText, do not commit the current hash again
  // or if route to be stored is credentials, do not commit the current hash
  // this saves from having to account for credentials in RJ and when `updateAndBack` adds back navigation complexity
  // which utilizes the progressStepText to determine 'back' route - DE41876 (UGMA Back from BYOP after credentials)
  const currentHashOrFalsy =
    currentRouteToHash() === lastHashInlastProgressStepText ||
    isCredentialsRoute
      ? ''
      : currentRouteToHash();

  const progressStepText = [lastProgressStepText, currentHashOrFalsy]
    .filter(truthy => truthy)
    .join(',');

  return progressStepText;
};

export const isJourneyStarted = (
  email: string | undefined,
  appID: string | undefined
): boolean => Boolean(email) || Boolean(appID);

/* eslint-disable complexity */
// TODO: simplify complexity
export const updateJourneyProfile: DataLoader = async ({
  store
}): Promise<PartialStateStore> => {
  const applicationIsComplete =
    store?.app?.applicationStatus === applicationStatusTypes.COMPLETED;
  const payloadWithoutProgress = convertAppDataToProfileData(store);
  const progressStepText = setProgressStepText(store);
  const readableProgressStepText = appendReadableStepString(progressStepText);

  // if no application id or email data exist then we shouldn't attempt to update profile; results in 400
  // specifically when application first loads
  if (
    !isJourneyStarted(
      payloadWithoutProgress?.emailAddress,
      payloadWithoutProgress?.applicationID
    )
  )
    return { progressStepText };

  // try block to check if profile service returns 401-Not authorized to view application
  // or 400 with a service error code of 80005 - Application already exists for account type
  try {
    await saveJourneyProfile({
      ...payloadWithoutProgress,
      // US582887 - MF Events
      // send account number(s) and confirmation last step with final RJ update
      progressStepText: applicationIsComplete
        ? `${progressStepText}:confirmation`
        : readableProgressStepText,
      ...(applicationIsComplete && {
        accountNumbers: parseAccountNumbers(store?.loadedData)
      })
    });
  } catch (err) {
    const authenticatedAppExists = applicationAlreadyExists(
      err.toString(),
      err.serviceErrorCode
    );

    // if profile requires login or already exists, and current user is not authorized to view,
    // then let the user continue thru the flow
    if (authenticatedAppExists) {
      // if the user is not logged in, set a flag which displays a modal and gives the user
      // an option to login
      const { guestUserApponFile = false } = store?.loadedData || {};
      if (!store?.authenticated && !guestUserApponFile) {
        return {
          progressStepText,
          loadedData: {
            guestUserApponFile: true
          }
        };
      }
      return { progressStepText };
    }

    // if the error has nothing to do with profile requiries login then - rethrow
    // eslint-disable-next-line
    console.log('new user profile', err);
    return { progressStepText };
  }

  return { progressStepText };
};
/* eslint-enable complexity */

export const createNewReturnJourneyProfile = async (
  storeToCommit
): Promise<StateStore> => {
  let profileApplicationID;
  try {
    profileApplicationID = (
      await createNewJourneyProfile(convertAppDataToProfileData(storeToCommit))
    )?.applicationID;
  } catch (err) {
    if (err instanceof TypeError) {
      return {
        ...storeToCommit,
        app: { ...storeToCommit.app, profileApplicationID }
      };
    }
    // if the error has nothing to do with the email address validation - rethrow
    throw new ServiceError(err);
  }
  return {
    ...storeToCommit,
    app: { ...storeToCommit.app, profileApplicationID }
  };
};

/* eslint-disable complexity */
// TODO: simplify complexity
export const updateTrustJourneyProfile: DataLoader = async ({
  store
}): Promise<PartialStateStore> => {
  const applicationIsComplete =
    store?.app?.applicationStatus === applicationStatusTypes.COMPLETED;
  const payloadWithoutProgress = convertAppDataToProfileData(store);
  const progressStepText = setProgressStepText(store);
  const readableProgressStepText = appendReadableStepString(progressStepText);

  // if no application id or email data exist then we shouldn't attempt to update profile; results in 400
  // specifically when application first loads
  if (
    !isJourneyStarted(
      payloadWithoutProgress?.emailAddress,
      payloadWithoutProgress?.applicationID
    )
  )
    return { progressStepText };

  // try block to check if profile service returns 401-Not authorized to view application
  try {
    await saveJourneyProfile({
      ...payloadWithoutProgress,
      // US582887 - MF Events
      // send account number(s) and confirmation last step with final RJ update
      progressStepText: applicationIsComplete
        ? `${progressStepText}:confirmation`
        : readableProgressStepText,
      ...(applicationIsComplete && {
        accountNumbers: parseAccountNumbers(store?.loadedData)
      })
    });
  } catch (err) {
    const authenticatedAppExists = Boolean(
      err.toString().match(/Not authorized to view application/)
    );
    // if profile requires login or already exists, and current user is not authorized to view,
    // then let the user continue thru the flow
    if (authenticatedAppExists) {
      // if the user is not logged in, set a flag which displays a modal and gives the user
      // an option to login
      const { guestUserApponFile = false } = store?.loadedData || {};
      if (!store?.authenticated && !guestUserApponFile) {
        return {
          progressStepText,
          loadedData: {
            guestUserApponFile: true
          }
        };
      }
      return { progressStepText };
    }
  }
  return { progressStepText };
};
/* eslint-enable complexity */

const preloadApplicationData = async (
  savedApplication
): Promise<PartialStateStore> => {
  // retrieve terms and conditions for review page
  const electronicAccessTerms = await loadElectronicAccessTerms();
  const electronicAccessDeliveryTerms = await loadElectronicAccessDeliveryTerms();
  const fiduciaryTerms = await loadFiduciaryDisclosureTerms();

  const {
    termsID: electronicAccessDeliveryTermsID,
    termsAndConditionParagraphs: electronicAccessDeliveryTermsParagraphs
  } = electronicAccessDeliveryTerms;
  const {
    termsID: electronicAccessTermsID,
    termsAndConditionParagraphs: electronicAccessTermsAndConditionParagraphs
  } = electronicAccessTerms;
  const {
    termsID: fiduciaryTermsID,
    termsAndConditionParagraphs: fiduciaryTermsAndConditionParagraphs
  } = fiduciaryTerms;

  const preloadedTermsData = {
    electronicAccessTerms: {
      electronicAccessTermsID
    },
    electronicAccessDelivery: {
      electronicAccessDeliveryTermsID,
      electronicAccessDeliveryTermsParagraphs
    },
    electronicAccess: {
      electronicAccessTermsID,
      electronicAccessTermsAndConditionParagraphs
    },
    fiduciary: {
      fiduciaryTermsID,
      fiduciaryTermsAndConditionParagraphs
    }
  };

  // retrieve existing agreements for all account types, exluding: Transfers, Trust, Joint and UGMA flows
  let existingAgreements = [];
  if (
    !(
      savedApplication.chargeType !== chargeTypes.TRANSFER &&
      (savedApplication.accountType === accountTypes.JOINT ||
        savedApplication.accountType === accountTypes.UGMAUTMA ||
        savedApplication.accountType === accountTypes.TRUST)
    )
  ) {
    const agreementTypes = getAgreementType(savedApplication);
    existingAgreements = await getExistingAgreementsService([agreementTypes]);
  }

  return {
    ...savedApplication,
    termsData: preloadedTermsData,
    progressStepText: removeAppendedReadableStepString(
      savedApplication?.progressStepText
    ),
    ...(existingAgreements?.length && { existingAgreements })
  };
};

const isV1Application = (name: string): boolean =>
  name !== consumerTypes.DEFAULT && name !== consumerTypes.FONASU;

export const rjRecordBeforeV5 = (existingProfile: any): boolean => {
  const consumerVersion = existingProfile?.consumerVersion || '0';
  const version = consumerVersion.split('@')?.pop();
  const intVersion = parseInt(version, 10);
  // default to true and route back to start should version parse fail (e.g. NaN)
  return !Number.isNaN(intVersion) ? intVersion < 5 : true;
};

const shouldRedirectToCLX = (loginRequired, authenticated): boolean =>
  loginRequired && !authenticated;

const checkSensitiveRoute = (
  { loginRequired },
  authenticated,
  sensitiveRoute
): boolean => !authenticated && !loginRequired && sensitiveRoute;

export const removeExcessHashes = (
  progressStepText: string | undefined,
  cutoffRoute: string
): string | undefined => {
  if (!progressStepText) return progressStepText;
  const cutoffHash =
    (routeMap || []).find(entry => entry?.fullRoutes?.includes(cutoffRoute))
      ?.hash?.short || '';
  const progressArray = progressStepText?.split(',');
  const cutoffIndex = progressArray?.indexOf(cutoffHash);
  const newProgressArray = progressArray?.splice(0, cutoffIndex + 1);
  return newProgressArray.join(',');
};

/* eslint-disable complexity */
// todo - fix complexity
export const putUserOnPath: DataLoader = async appState => {
  const { store, chosen: application } = appState;
  // account for login required flag from both service data & rj email links
  // loginRequired - pulled from application data if fetched from profile service
  // loginrequired (note capitialization diff) - is pulled out of the url from RJ emails
  // ref - DE41060
  const { applicationID, consumerName, loginRequired = false } = application;
  const { loginrequired = false } = getQueryStringData();

  // nasu v1 logic.
  if (isV1Application(consumerName)) {
    redirectToAccountFinder();
    return {};
  }

  // nasu AE logic
  const { authenticated } = store;
  // account for whether login is required from the application data or rj email link
  const requiresLogin = loginRequired || loginrequired;
  // here we redirect to CLX+++'
  if (shouldRedirectToCLX(requiresLogin, authenticated)) {
    const url = `${config(
      'clxUrl'
    )}/usis/login/?pdurl=/clx-tk/token-exchange/redirect/nasu?sdurl=${config(
      'returnUrlPrefix'
    )}personal-investing/tools/nasu/return-journey/welcome?applicationID=${applicationID}`;
    navigateToExternal(url);
    return {};
  }

  const savedApplication = await getJourneyProfile({
    profileID: applicationID,
    store
  });

  // pre-load necessary data and update saved application object
  // current list - as of 4/26/2022
  // terms & conditions, existing banks, daily purchase limits + daily customer purchases
  const preloadedSavedApplication = await preloadApplicationData(
    savedApplication
  );

  const { progressStepText = '' } = preloadedSavedApplication;

  const sensitiveRoute = getSensitiveRoute(progressStepText);
  const lastVisitedRoute = getLastVisitedRoute(progressStepText);

  // if user was not logged in at time of application
  // and reached the sensitiveNode (e.g. PersonalInformation)
  // send them there
  if (checkSensitiveRoute(application, authenticated, sensitiveRoute)) {
    // progressStepText has to remove all hashes after the special route they are being navigated to
    const specialNavProgressStepText = removeExcessHashes(
      progressStepText,
      sensitiveRoute
    );
    return {
      ...preloadedSavedApplication,
      routeData: { rjRoute: sensitiveRoute },
      progressStepText: specialNavProgressStepText
    };
  }

  // intercept RJs that had funding, but have been converted now to non-funded flows
  // route them back to the first  screen (BasicPI/Profile)
  if (
    isNonFundedFlow(preloadedSavedApplication?.accountType) &&
    rjRecordBeforeV5(savedApplication)
  ) {
    return {
      ...preloadedSavedApplication,
      routeData: { rjRoute: sensitiveRoute },
      progressStepText: ''
    };
  }

  // put user on last visited page
  return {
    ...preloadedSavedApplication,
    routeData: { rjRoute: lastVisitedRoute }
  };
};

putUserOnPath.message =
  'Restoring data, and rerouting to your saved application.';
/* eslint-enable complexity */

export const loadProfileIDs: DataLoader = async (
  state
): Promise<StateStoreOrVoid> => {
  const { email, applicationID } = getQueryStringData();

  // get data for authenticated users
  const userState: StateStoreOrVoid = await loadUser(state);

  const profiles = applicationID ? [] : await getAllApplicationsByEmail(email);

  return mergeTyped<StateStoreOrVoid>(userState, {
    loadedData: { profiles }
  });
};

export const loadUnsubscribe: DataLoader = async (): Promise<StateStoreOrVoid> => {
  const searchParams = new URLSearchParams(window.location.search);
  const applicationID = searchParams.get('applicationID') || '';
  const unsubscribePayload = {
    applicationID
  };

  try {
    await unsubscribeFromRJProfile(unsubscribePayload);
    return {
      loadedData: {
        RJUnsubscribeSuccessful: true
      }
    };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Failed to unsubscribe due to', err);
    return {
      loadedData: {
        RJUnsubscribeStatus: false
      }
    };
  }
};

loadProfileIDs.message = 'Loading Saved Applications';

export const updateAnalytics: DataLoader = async ({ store }, flowProps) => {
  analyticsDataLayer({
    sourceType: getAppSourceType(store?.consumerName),
    userType:
      store?.authenticated && !store?.loadedData?.newUserCreated
        ? 'Existing'
        : 'New'
  });
  dispatchPageLoad(flowProps?.analytics.pageName);
};
